import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import api, { queryClient } from '@/api';
import { isErrorCode } from '@/api/errors';
import { AlertBox } from '@/common/AlertBox';
import { InfoSectionContainer } from '@/common/InfoSection';
import { TabsBar } from '@/common/TabsBar';
import { WBSButton } from '@/common/WBSButton';
import { FormTitle, PageContent, PageHeader } from '@/settings/settingsStyles';
import { getMemberSettingsTabs } from '@/settings/tabData';
import { Title } from '~/styles/layoutElements';

export const Route = createFileRoute(
  '/_app/_users/members/$memberId/settings/export',
)({
  component: ExportDataRoute,
});

/**
 * A member has at most one open request. The server enforces that (409
 * `data_export_request_pending`), so a request made in another tab lands on
 * the same notice rather than an error.
 */
function ExportDataRoute() {
  const { t } = useTranslation();
  const { memberId } = Route.useParams();

  const { data: latest } = api.getMyDataExportRequest.useQuery();
  const create = api.createMyDataExportRequest.useMutation(undefined, {
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: api.getMyDataExportRequest.getQueryKey(),
      });
    },
  });

  const open =
    latest?.status === 'PENDING' ||
    latest?.status === 'QUEUED' ||
    latest?.status === 'FAILED';
  const alreadyPending = isErrorCode(
    create.error,
    'data_export_request_pending',
  );
  const notified = open || create.isSuccess || alreadyPending;
  const failed = create.isError && !alreadyPending;

  return (
    <PageContent>
      <PageHeader>
        <Title>{t('wbs.account-settings')}</Title>
      </PageHeader>

      <div style={{ marginBottom: 11 }}>
        <TabsBar
          tabs={getMemberSettingsTabs(t)}
          base={`/members/${memberId}/settings`}
          selected="/export"
        />
      </div>
      <InfoSectionContainer>
        <FormTitle>{t('wbs.request-an-export-of')}</FormTitle>
        {notified && (
          <AlertBox variant="info">
            {t('notifications.the-site-administrator-has')}
          </AlertBox>
        )}
        {failed && (
          <AlertBox variant="error">
            {t('notifications.unable-to-initiate-the')}
          </AlertBox>
        )}
        {!notified && (
          <WBSButton
            wide
            isLoading={create.isPending}
            onClick={() => create.mutate({})}
            style={{ margin: '22px 0 19px 0' }}
          >
            {t('wbs.request-data-export')}
          </WBSButton>
        )}
      </InfoSectionContainer>
    </PageContent>
  );
}
